import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';
import { ForcePublishReceiverResponseModel } from './models/force-publish-receiver-response-model';

@Injectable({
  providedIn: 'root',
})
export class ForcePublishReceiversAPIService extends BehaviorSubject<GridDataResult> {
    public baseUrlLocal: string = environment.apiBaseUrl;
    public loading: boolean = false;

    constructor(protected http: HttpClient) {
        super({ data: [], total: null });
    }

    public query(state: State, filter: CompositeFilterDescriptor): void {
        this.loading = true;
        this.fetch(state, filter).subscribe((x) => super.next(x));
    }

    protected fetch(state: State, filter: CompositeFilterDescriptor): Observable<GridDataResult> {
        let queryStart = '';
        let queryString = '';

        queryStart = this.getQueryStart(queryStart);
        queryString = queryString.concat(`${queryStart}pageindex=${state.skip}`);
        queryStart = this.getQueryStart(queryString);
        queryString = queryString.concat(`${queryStart}pagesize=${state.take}`);

        const receiverFilter = this.getStateFilterValue(filter, 'receiver');
        if (receiverFilter != undefined && receiverFilter.length >= 3) {
            queryStart = this.getQueryStart(queryString);
            queryString = queryString.concat(`${queryStart}receiver=${encodeURIComponent(receiverFilter)}`);
        }

        return this.http.get<GridDataResult>(this.baseUrlLocal + `forcePublishReceivers/search` + queryString)
          .pipe(
              map(
                  (response: GridDataResult) => {
                      return {
                          data: response.data,
                          total: response.total
                      };
                  }
              ),
              tap(() => (this.loading = false))
          );
    }

    private getQueryStart(queryString: string): string {
        return queryString === '' ? '?' : '&';
    }

    private getStateFilterValue(filter: CompositeFilterDescriptor, field: string): string | undefined {
        if (!filter?.filters?.length) {
            return undefined;
        }
        const matchingFilter = filter.filters.find((item): item is FilterDescriptor => {
            return !('filters' in item) && item.field === field;
        });
        if (matchingFilter?.value === undefined || matchingFilter.value === null) {
            return undefined;
        }
        return String(matchingFilter.value);
    }

    public getAll(): Observable<ForcePublishReceiverResponseModel[]> {
        return this.http.get<GridDataResult>(this.baseUrlLocal + `forcePublishReceivers/search?pageindex=0&pagesize=1000`)
            .pipe(map((response: GridDataResult) => response.data as ForcePublishReceiverResponseModel[]));
    }

    public create(model: ForcePublishReceiverResponseModel): Observable<ForcePublishReceiverResponseModel> {
        return this.http.post<ForcePublishReceiverResponseModel>(this.baseUrlLocal + `forcePublishReceivers`, model);
    }

    public update(id: number, model: ForcePublishReceiverResponseModel): Observable<ForcePublishReceiverResponseModel> {
        return this.http.put<ForcePublishReceiverResponseModel>(this.baseUrlLocal + `forcePublishReceivers/${id}`, model);
    }

    public delete(id: number): Observable<void> {
        return this.http.delete<void>(this.baseUrlLocal + `forcePublishReceivers/${id}`);
    }
}

@Injectable()
export class ForcePublishReceiversService extends ForcePublishReceiversAPIService {
    constructor(http: HttpClient) {
        super(http);
    }
}
